import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usersApi } from '../../../api/admin/users';
import { ApiError } from '../../../api/client';
import { IconActionButton } from '../../../components/IconActionButton';
import { Power } from 'lucide-react';
import { DataTable } from '../../../components/DataTable';
import { Modal } from '../../../components/Modal';
import { useToast } from '../../../components/Toast';
import { usePermission } from '../../../auth/AuthContext';

// Mirrors the backend's createSchema (admin-user.controller.ts) for
// instant feedback only - the backend's Zod validation and the
// duplicate-email check (ER_DUP_ENTRY -> clean 422) remain the actual
// authority.
const formSchema = z.object({
  fullName: z.string().min(2, 'Name must be at least 2 characters.'),
  email: z.string().email('Enter a valid email address.'),
});
type FormValues = z.infer<typeof formSchema>;

export function UsersPage() {
  const canManage = usePermission('admin.users.manage');
  const [modalOpen, setModalOpen] = useState(false);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const usersQuery = useQuery({ queryKey: ['users'], queryFn: () => usersApi.list() });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(formSchema) });

  const createMutation = useMutation({
    mutationFn: (values: FormValues) => usersApi.create(values),
    onSuccess: (result) => {
      showSuccess('User created. They will be fully recognized the first time they sign in with Microsoft.');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setModalOpen(false);
      reset();
      // Land directly on the new user's detail page so roles/department/
      // position/approval limits can be configured immediately, per the
      // requested flow - no need to find them in the list first.
      navigate(`/admin/users/${result.data.id}`);
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create user.'),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: number) => usersApi.deactivate(id),
    onSuccess: () => {
      showSuccess('User deactivated.');
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    // Surfaces the last-admin-protection guard's real message verbatim
    // if this was the only active administrator.
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to deactivate user.'),
  });

  const activateMutation = useMutation({
    mutationFn: (id: number) => usersApi.activate(id),
    onSuccess: () => {
      showSuccess('User activated.');
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to activate user.'),
  });

  return (
    <div className="page">
      <div className="page-header">
        <h1>Users</h1>
        {canManage && <button onClick={() => setModalOpen(true)}>Add User</button>}
      </div>

      {usersQuery.isLoading && <p>Loading…</p>}
      {usersQuery.isError && <p className="error-text">Could not load users.</p>}

      {usersQuery.data && (
        <DataTable
          rows={usersQuery.data.data}
          rowKey={(u) => u.id}
          emptyMessage="No users yet."
          columns={[
            { header: 'Name', render: (u) => <Link to={`/admin/users/${u.id}`}>{u.full_name}</Link> },
            { header: 'Email', render: (u) => u.email },
            { header: 'Entra Department', render: (u) => u.department ?? '-' },
            { header: 'Status', render: (u) => (u.is_active ? 'Active' : 'Inactive') },
            {
              header: '',
              render: (u) =>
                canManage ? (
                  u.is_active ? (
                    <IconActionButton icon={Power} label="Deactivate" onClick={() => deactivateMutation.mutate(u.id)} />
                  ) : (
                    <IconActionButton icon={Power} label="Activate" onClick={() => activateMutation.mutate(u.id)} />
                  )
                ) : null,
            },
          ]}
        />
      )}

      {modalOpen && (
        <Modal title="Add User" onClose={() => setModalOpen(false)}>
          <p className="muted">
            This creates a placeholder record. The person isn't fully recognized in VConnect until they sign in with
            Microsoft for the first time - at that point their record is automatically matched by this email address.
            You can assign roles, department, and position right away without waiting for that.
          </p>
          <form onSubmit={handleSubmit((values) => createMutation.mutate(values))}>
            <label>
              Full name
              <input {...register('fullName')} />
              {errors.fullName && <span className="field-error">{errors.fullName.message}</span>}
            </label>
            <label>
              Email
              <input type="email" {...register('email')} />
              {errors.email && <span className="field-error">{errors.email.message}</span>}
            </label>
            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Creating…' : 'Create'}
              </button>
              <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

